/**
 * Presentation-only pure helpers for the round HUD.
 *
 * Kept separate from `RoundHud` itself (which touches the DOM) so the logic
 * that decides *what* the HUD says can be tested without a browser — the same
 * split the shared package uses between `round.ts`'s pure functions and
 * whatever consumes them.
 */

import {
  RoundPhase,
  canMove,
  type RoundPhaseId,
  type TeamId,
  Team,
} from "@prop-hunt/shared";

export interface TeamCounts {
  hunters: number;
  /** Hiders not yet found — the number that decides the round. */
  hiders: number;
  /** Hiders found so far, so the HUD can show progress rather than a decrement. */
  hidersFound: number;
  unassigned: number;
}

/**
 * Hunter/hider counts, derived from the team map and the found set.
 *
 * `hiders` is a survivor count now rather than a roster count. Until M4 there
 * was no way to leave the round except by disconnecting, so the two were the
 * same number and the HUD label had to admit it; the number that decides the
 * round is the survivor one, and it is the one worth showing.
 */
export function countTeams(
  teams: ReadonlyMap<number, TeamId>,
  found: ReadonlySet<number>,
): TeamCounts {
  let hunters = 0;
  let hiders = 0;
  let hidersFound = 0;
  let unassigned = 0;
  for (const [id, team] of teams) {
    if (team === Team.Hunter) {
      hunters += 1;
    } else if (team === Team.Hider) {
      if (found.has(id)) hidersFound += 1;
      else hiders += 1;
    } else {
      unassigned += 1;
    }
  }
  return { hunters, hiders, hidersFound, unassigned };
}

/**
 * What the "you can't move" banner should say, or `null` if the player is
 * free to move.
 *
 * Delegates the actual decision to `canMove` — this only turns "no" into
 * words. A frozen hunter during Hiding is the game's whole asymmetry, so it
 * gets the plain name; Over freezes everyone for the scoreboard beat, which
 * is a different situation and reads better with a different word.
 */
export function heldMessage(
  phase: RoundPhaseId,
  team: TeamId,
  secondsLeft: number | null,
  found: boolean,
): string | null {
  if (canMove(phase, team, found)) return null;
  const countdown = secondsLeft === null ? "" : ` · ${secondsLeft}s`;
  // Being found outranks the phase in the banner: a found hider is held for
  // the rest of the round rather than for the rest of a countdown, and showing
  // them the phase timer would read as "you can move again in 12 seconds".
  if (found) return "FOUND · spectating";
  if (phase === RoundPhase.Over) return `ROUND OVER${countdown}`;
  return `HELD${countdown}`;
}
